import { useAppContext } from '@faro/app-context';
import {
    CommandResponse,
    PlacementPayload,
    RenamePayload,
    RevisionedCommandResponse,
    SchemaSection,
    StudyEntityType,
} from '@faro/study-designer-model';
import { createSectionDuplicateMutation, SectionDuplicateHook } from '../createSectionDuplicateMutation';
import { createSectionInsertMutation, SectionInsertHook } from '../createSectionInsertMutation';
import {
    useSchemaSectionDelete,
    useSchemaSectionLink,
    useSchemaSectionMove,
    useSchemaSectionRename,
    useSchemaSectionUpdate,
} from './SchemaSectionHooks';

export type SchemaSectionInsertHook = SectionInsertHook<Partial<SchemaSection>>;

/**
 * Mutation hook to insert a schema section.
 * @returns A function that inserts a schema section.
 */
export const useSchemaSectionInsertMutation: (studyId: string) => SchemaSectionInsertHook = createSectionInsertMutation<
    Partial<SchemaSection>
>(StudyEntityType.SchemaSection, 'schema-section', 'Failed to create schema section');

export type SchemaSectionUpdateHook = (
    schemaSection: SchemaSection
) => Promise<RevisionedCommandResponse<CommandResponse>>;

/**
 * Mutation hook to update a schema section.
 * @returns A function that updates a schema section.
 */
export function useSchemaSectionUpdateMutation(studyId: string): SchemaSectionUpdateHook {
    const { showErrorHandler } = useAppContext();

    const updateSchemaSection = useSchemaSectionUpdate({ studyId });

    function update(schemaSection: SchemaSection) {
        return updateSchemaSection
            .mutateAsync(schemaSection)
            .catch(showErrorHandler('Failed to update schema section'));
    }

    return update;
}

export type SchemaSectionDeleteHook = (schemaSection: {
    id: string;
}) => Promise<RevisionedCommandResponse<CommandResponse>>;

/**
 * Mutation hook to delete a schema section.
 * @returns A function that deletes a schema section.
 */
export function useSchemaSectionDeleteMutation(studyId: string): SchemaSectionDeleteHook {
    const { showErrorHandler } = useAppContext();

    const deleteSchemaSection = useSchemaSectionDelete({ studyId });

    function deleteSchema(schemaSection: { id: string }) {
        return deleteSchemaSection
            .mutateAsync({
                id: schemaSection.id,
                studyId,
            })
            .catch(showErrorHandler('Failed to delete schema section'));
    }

    return deleteSchema;
}

export type SchemaSectionDuplicateHook = SectionDuplicateHook;

/**
 * Mutation hook to duplicate a schema section.
 * @returns A function that duplicates a schema section.
 */
export const useSchemaSectionDuplicateMutation: (studyId: string) => SchemaSectionDuplicateHook =
    createSectionDuplicateMutation(
        StudyEntityType.SchemaSection,
        'schema-section',
        'Failed to duplicate schema section'
    );

export type SchemaSectionMoveHook = (schemaSection: {
    id: string;
    placement: PlacementPayload;
}) => Promise<RevisionedCommandResponse<CommandResponse>>;

/**
 * Mutation hook to move a schema section.
 * @returns A function that moves a schema section.
 */
export function useSchemaSectionMoveMutation(studyId: string): SchemaSectionMoveHook {
    const { showErrorHandler } = useAppContext();

    const moveSchemaSection = useSchemaSectionMove({ studyId });

    function move(schemaSection: { id: string; placement: PlacementPayload }) {
        return moveSchemaSection
            .mutateAsync({
                ...schemaSection,
                studyId,
            })
            .catch(showErrorHandler('Failed to move schema section'));
    }

    return move;
}

export type SchemaSectionLinkHook = (schemaSection: {
    id: string;
    linkedSection: Partial<SchemaSection> & Pick<SchemaSection, 'id' | 'studyId' | 'entityType'>;
}) => Promise<RevisionedCommandResponse<CommandResponse>>;

/**
 * Mutation hook to link a schema section.
 * @returns A function that links a schema section.
 */
export function useSchemaSectionLinkMutation(studyId: string): SchemaSectionLinkHook {
    const { showErrorHandler } = useAppContext();

    const linkSchemaSection = useSchemaSectionLink({ studyId });

    function link(schemaSection: {
        id: string;
        linkedSection: Partial<SchemaSection> & Pick<SchemaSection, 'id' | 'studyId' | 'entityType'>;
    }) {
        return linkSchemaSection
            .mutateAsync({
                ...schemaSection,
                studyId,
            })
            .catch(showErrorHandler('Failed to link schema section'));
    }

    return link;
}

export type SchemaSectionRenameHook = (schemaSection: {
    id: string;
    renamePayload: RenamePayload;
}) => Promise<RevisionedCommandResponse<CommandResponse>>;

/**
 * Mutation hook to rename a schema section.
 * @returns A function that renames a schema section.
 */
export function useSchemaSectionRenameMutation(studyId: string): SchemaSectionRenameHook {
    const { showErrorHandler } = useAppContext();

    const renameSchemaSection = useSchemaSectionRename({ studyId });

    function rename(schemaSection: { id: string; renamePayload: RenamePayload }) {
        return renameSchemaSection
            .mutateAsync({
                ...schemaSection,
                studyId,
            })
            .catch(showErrorHandler('Failed to rename schema section'));
    }

    return rename;
}
